/**
 * Phase Rentab-1+2-B (13/09/2026) — Card main-d'œuvre & sous-traitance chantier.
 *
 * Positionnée dans `ChantierPage.jsx` entre Workflow et Invoices.
 *
 * RBAC granulaire côté client (mirror du RBAC backend `patch_chantier`) :
 *   - `labor_days_estimated`         → admin only (readOnly sinon, jamais caché)
 *   - `labor_days_actual`            → admin + gestionnaire_chantier
 *   - `subcontracting_estimated_htva`→ admin only
 *   - `subcontracting_billing_finalized` → admin only (toggle)
 *
 * Composant invisible si l'utilisateur n'a aucun droit de lecture (ex: commercial).
 * Le backend applique le RBAC final — le RBAC client ne fait qu'améliorer l'UX.
 *
 * Doctrine widgets contenu : chaque valeur affichée doit correspondre à l'API.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, HardHat, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";

const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

// Rôles autorisés à VOIR cette Card (autres = null). Le RBAC final est backend.
const READ_ROLES = ["admin", "secretaire", "gestionnaire_chantier"];

export default function ChantierLaborAndSubcontractingCard({ chantier, onSaved }) {
  const { state } = useAuth();
  const user = state?.user;
  const canReadCard = hasRole(user, READ_ROLES);
  const canEditLaborActual = hasRole(user, ["admin", "gestionnaire_chantier"]);
  const canEditLaborEstimated = hasRole(user, ["admin"]);
  const canEditSubcontracting = hasRole(user, ["admin"]);

  const [form, setForm] = useState({
    labor_days_estimated: chantier?.labor_days_estimated ?? "",
    labor_days_actual: chantier?.labor_days_actual ?? "",
    subcontracting_estimated_htva: chantier?.subcontracting_estimated_htva ?? "",
    subcontracting_billing_finalized: !!chantier?.subcontracting_billing_finalized,
  });
  const [saving, setSaving] = useState(false);
  const [supplierCosts, setSupplierCosts] = useState(null);

  useEffect(() => {
    setForm({
      labor_days_estimated: chantier?.labor_days_estimated ?? "",
      labor_days_actual: chantier?.labor_days_actual ?? "",
      subcontracting_estimated_htva: chantier?.subcontracting_estimated_htva ?? "",
      subcontracting_billing_finalized: !!chantier?.subcontracting_billing_finalized,
    });
  }, [chantier]);

  const loadSubCosts = useCallback(async () => {
    if (!canReadCard || !chantier?.id) return;
    try {
      const { data } = await api.get(`/chantiers/${chantier.id}/margin`);
      setSupplierCosts(data);
    } catch (e) {
      // 403 → utilisateur non-admin/secrétaire (endpoint marge nécessite admin+ + gestionnaire_chantier)
      if (e?.response?.status !== 403) {
        // Silent : la Card reste utilisable même sans la marge
      }
    }
  }, [canReadCard, chantier?.id]);

  useEffect(() => { loadSubCosts(); }, [loadSubCosts]);

  const patchField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/chantiers/${chantier.id}`, patch);
      toast.success("Chantier mis à jour.");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const parseFloatOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Save labor_days_estimated
  const saveLaborEstimated = () => {
    if (!canEditLaborEstimated) return;
    patchField({ labor_days_estimated: parseFloatOrNull(form.labor_days_estimated) });
  };
  const saveLaborActual = () => {
    if (!canEditLaborActual) return;
    patchField({ labor_days_actual: parseFloatOrNull(form.labor_days_actual) });
  };
  const saveSubEstimated = () => {
    if (!canEditSubcontracting) return;
    patchField({ subcontracting_estimated_htva: parseFloatOrNull(form.subcontracting_estimated_htva) });
  };
  const toggleFinalized = (checked) => {
    if (!canEditSubcontracting) return;
    setForm((f) => ({ ...f, subcontracting_billing_finalized: checked }));
    patchField({ subcontracting_billing_finalized: !!checked });
  };

  const laborActualRounded = useMemo(() => {
    const v = supplierCosts?.labor_cost;
    return Number.isFinite(v) ? v : 0;
  }, [supplierCosts]);
  const subActualHtva = supplierCosts?.total_subcontracting_actual_htva ?? 0;
  const subRetained = supplierCosts?.subcontracting_cost_retained ?? 0;
  const hasSubcontractingInvoices = subActualHtva > 0;
  const showBillingInProgressBadge = hasSubcontractingInvoices && !form.subcontracting_billing_finalized;

  if (!canReadCard) return null;

  return (
    <Card
      className="border-slate-800 bg-slate-900/40"
      data-testid="chantier-labor-and-subcontracting-card"
    >
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-[#C4553B]" />
            <div>
              <h2 className="text-sm font-semibold text-slate-100">
                Main-d&apos;œuvre & sous-traitance
              </h2>
              <p className="text-xs text-slate-500">
                Coûts internes chantier — impactent la marge sur achats.
              </p>
            </div>
          </div>
          {showBillingInProgressBadge && (
            <Badge
              className="border-amber-500/60 bg-amber-500/10 text-amber-300"
              data-testid="badge-subcontracting-billing-in-progress"
            >
              <AlertCircle className="mr-1 h-3 w-3" />
              Sous-traitance en cours de facturation
            </Badge>
          )}
        </div>

        {/* Main-d'œuvre — Prévu / Réalisé */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">
              Jours main-d&apos;œuvre prévus
              {!canEditLaborEstimated && (
                <span className="ml-2 text-[10px] text-slate-500 normal-case">
                  (lecture seule — admin only)
                </span>
              )}
            </Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              inputMode="decimal"
              value={form.labor_days_estimated}
              disabled={!canEditLaborEstimated || saving}
              readOnly={!canEditLaborEstimated}
              onChange={(e) =>
                setForm({ ...form, labor_days_estimated: e.target.value })
              }
              onBlur={saveLaborEstimated}
              className="border-slate-700 bg-slate-950/40 text-slate-100 tabular-nums"
              data-testid="input-labor-days-estimated"
              placeholder="—"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">
              Jours main-d&apos;œuvre réalisés
              {!canEditLaborActual && (
                <span className="ml-2 text-[10px] text-slate-500 normal-case">(lecture seule)</span>
              )}
            </Label>
            <Input
              type="number"
              step="0.5"
              min="0"
              inputMode="decimal"
              value={form.labor_days_actual}
              disabled={!canEditLaborActual || saving}
              readOnly={!canEditLaborActual}
              onChange={(e) => setForm({ ...form, labor_days_actual: e.target.value })}
              onBlur={saveLaborActual}
              className="border-slate-700 bg-slate-950/40 text-slate-100 tabular-nums"
              data-testid="input-labor-days-actual"
              placeholder="—"
            />
          </div>
        </div>

        {/* Récap coût main-d'œuvre calculé */}
        {laborActualRounded > 0 && (
          <div
            className="rounded border border-slate-800 bg-slate-950/40 p-3 text-xs text-slate-400"
            data-testid="labor-cost-recap"
          >
            Coût main-d&apos;œuvre calculé :{" "}
            <span
              className="font-semibold tabular-nums text-slate-200"
              data-testid="labor-cost-recap-value"
            >
              {money(laborActualRounded)}
            </span>{" "}
            <span className="text-slate-500">
              (source : {supplierCosts?.labor_cost_source === "actual"
                ? "jours réalisés"
                : supplierCosts?.labor_cost_source === "estimated"
                ? "jours prévus (à défaut)"
                : "—"})
            </span>
          </div>
        )}

        {/* Sous-traitance */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">
              Sous-traitance prévue (HTVA)
              {!canEditSubcontracting && (
                <span className="ml-2 text-[10px] text-slate-500 normal-case">
                  (lecture seule — admin only)
                </span>
              )}
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={form.subcontracting_estimated_htva}
              disabled={!canEditSubcontracting || saving}
              readOnly={!canEditSubcontracting}
              onChange={(e) =>
                setForm({ ...form, subcontracting_estimated_htva: e.target.value })
              }
              onBlur={saveSubEstimated}
              className="border-slate-700 bg-slate-950/40 text-slate-100 tabular-nums"
              data-testid="input-subcontracting-estimated-htva"
              placeholder="—"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs uppercase tracking-wide">
              Sous-traitance réelle (HTVA facturé)
            </Label>
            <div
              className="rounded border border-slate-800 bg-slate-950/60 px-3 py-2 text-sm tabular-nums text-slate-100"
              data-testid="subcontracting-actual-htva"
            >
              {money(subActualHtva)}
              {supplierCosts?.subcontracting_cost_mode && (
                <span className="ml-2 text-[10px] text-slate-500">
                  (retenu : {money(subRetained)} ·{" "}
                  {supplierCosts.subcontracting_cost_mode === "max_pessimistic"
                    ? "MAX prévu/réel"
                    : supplierCosts.subcontracting_cost_mode === "actual_finalized"
                    ? "réel seul (facturation finalisée)"
                    : supplierCosts.subcontracting_cost_mode === "estimated_only"
                    ? "prévu seul"
                    : supplierCosts.subcontracting_cost_mode === "actual_only"
                    ? "réel seul"
                    : "aucun"}
                  )
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Toggle finalized */}
        <label
          className={`flex cursor-pointer items-start gap-3 rounded-md border border-slate-800 bg-slate-950/40 p-3 ${
            canEditSubcontracting ? "hover:border-slate-700" : "opacity-70 cursor-not-allowed"
          }`}
        >
          <input
            type="checkbox"
            checked={!!form.subcontracting_billing_finalized}
            disabled={!canEditSubcontracting || saving}
            onChange={(e) => toggleFinalized(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-950 text-[#C4553B] focus:ring-[#C4553B]"
            data-testid="toggle-subcontracting-billing-finalized"
          />
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium text-slate-200">
              Sous-traitance facturée à 100&nbsp;% (basculer sur réel dans la marge)
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Décoché (défaut) : la marge retient le MAX(prévu, réel) — hypothèse pessimiste
              tant que toutes les factures ne sont pas rentrées. Coché : la marge retient
              uniquement le réel facturé. Auto-décoché à chaque nouvelle facture sous-traitance
              rattachée à ce chantier.
            </p>
          </div>
        </label>

        {saving && (
          <div className="text-xs text-slate-500 flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Enregistrement…
          </div>
        )}
      </CardContent>
    </Card>
  );
}
